import React, { useState, useEffect, useCallback } from 'react';
import { Card, Alert, Button, Spin } from 'antd';
import Icon, { 
	CodeSandboxOutlined, 
	PlusOutlined, 
	ExclamationCircleOutlined,
	CloudServerOutlined,
	LaptopOutlined,
} from '@ant-design/icons';
import { OverviewMetrics } from '@src/api/deployment/deploymentApi';
import deploymentApi from '@src/api/deployment/deploymentApi';
import useTranslation from '@hooks/useTranslation';
import styles from './ManageEnvironmentsModal.module.css';
import { ReactComponent as TerminalIcon } from '@src/assets/img/interface/terminal.svg';

type OverviewTabProps = {
	visible?: boolean;
	onNavigateToTab: (tab: string) => void;
	onCreateEnvironment: () => void;
};

/**
 * Overview tab component displaying metrics and quick actions
 * @param visible - Whether the tab is currently visible
 * @param onNavigateToTab - Callback to navigate to another tab
 * @param onCreateEnvironment - Callback to open create environment modal
 */
const OverviewTab: React.FC<OverviewTabProps> = ({
	visible,
	onNavigateToTab,
	onCreateEnvironment,
}) => {
	const t = useTranslation('ManageEnvironmentsModal');
	const [metrics, setMetrics] = useState<OverviewMetrics | null>(null);
	const [loading, setLoading] = useState(false);

	/**
	 * Loads overview metrics from the API
	 */
	const loadOverviewMetrics = useCallback(async () => {
		setLoading(true);
		try {
			const data = await deploymentApi.getOverviewMetrics();
			setMetrics(data);
		} catch (err) {
			console.error('Failed to load overview metrics:', err);
			// Set default values on error
			setMetrics({
				environments: 0,
				hosts: 0,
				deployments: 0,
				processes: 0,
				failedProcesses: 0,
			});
		} finally {
			setLoading(false);
		}
	}, []);

	// Load metrics when tab becomes visible
	useEffect(() => {
		if (visible) {
			loadOverviewMetrics();
		}
	}, [visible, loadOverviewMetrics]);

	const displayMetrics = metrics || {
		environments: 0,
		hosts: 0,
		deployments: 0,
		processes: 0,
		failedProcesses: 0,
	};

	// Calculate total deployments and processes for subtitles
	const totalDeployments = displayMetrics.deployments;
	const totalProcesses = displayMetrics.processes;

	return (
		<Spin spinning={loading}>
			<div className={styles.OverviewContainer}>
				{/* Metric Cards */}
				<div className={styles.MetricsGrid}>
				<Card className={styles.MetricCard}>
					<div className={styles.MetricCardContent}>
						<CloudServerOutlined className={styles.MetricIcon} />
						<div className={styles.MetricValueContainer}>
							<div className={styles.MetricValue}>{displayMetrics.environments}</div>
							<div className={styles.MetricLabel}>{t('Overview.Metrics.Environments')}</div>
						</div>
					</div>
				</Card>
				<Card className={styles.MetricCard}>
					<div className={styles.MetricCardContent}>
						<LaptopOutlined className={styles.MetricIcon} />
						<div className={styles.MetricValueContainer}>
							<div className={styles.MetricValue}>{displayMetrics.hosts}</div>
							<div className={styles.MetricLabel}>{t('Overview.Metrics.Hosts')}</div>
						</div>
					</div>
				</Card>
				<Card className={`${styles.MetricCard} ${styles.MetricCardDeployments}`}>
					<div className={styles.MetricCardContent}>
						<CodeSandboxOutlined className={styles.MetricIcon} />
						<div className={styles.MetricValueContainer}>
							<div className={styles.MetricValue}>{displayMetrics.deployments}</div>
							<div className={styles.MetricLabel}>{t('Overview.Metrics.Deployments')}</div>
							{totalDeployments > 0 && (
								<div className={styles.MetricSubtitle}>
									{t('Overview.Metrics.Deployments.Subtitle', { count: totalDeployments })}
								</div>
							)}
						</div>
					</div>
				</Card>
				<Card className={`${styles.MetricCard} ${displayMetrics.failedProcesses > 0 ? styles.MetricCardWithFailedProcesses : ''}`}>
					<div className={styles.MetricCardContent}>
						<Icon component={TerminalIcon} className={styles.MetricIcon} />
						<div className={styles.MetricValueContainer}>
							<div className={styles.MetricValue}>{displayMetrics.processes}</div>
							<div className={styles.MetricLabel}>{t('Overview.Metrics.Processes')}</div>
							{displayMetrics.failedProcesses > 0 && (
								<div className={styles.MetricSubtitle}>
									{t('Overview.Metrics.Processes.Subtitle', { count: displayMetrics.failedProcesses })}
								</div>
							)}
						</div>
					</div>
				</Card>
			</div>

			{/* Quick Actions */}
			<div className={styles.QuickActionsGrid}>
				<Card 
					className={styles.QuickActionCard}
					hoverable
					onClick={() => onNavigateToTab('deployments')}
				>
					<div className={styles.QuickActionContent}>
						<CodeSandboxOutlined className={styles.QuickActionIcon} />
						<div className={styles.QuickActionTitle}>{t('Overview.QuickActions.ViewAllDeployments')}</div>
						<div className={styles.QuickActionSubtitle}>
							{t('Overview.QuickActions.ViewAllDeployments.Subtitle', { count: totalDeployments })}
						</div>
					</div>
				</Card>
				<Card 
					className={styles.QuickActionCard}
					hoverable
					onClick={() => onNavigateToTab('processes')}
				>
					<div className={styles.QuickActionContent}>
						<Icon component={TerminalIcon} className={styles.QuickActionIcon} />
						<div className={styles.QuickActionTitle}>{t('Overview.QuickActions.ViewAllProcesses')}</div>
						<div className={styles.QuickActionSubtitle}>
							{t('Overview.QuickActions.ViewAllProcesses.Subtitle', { count: totalProcesses })}
						</div>
					</div>
				</Card>
				<Card 
					className={`${styles.QuickActionCard} ${styles.QuickActionCardNew}`}
					hoverable
					onClick={onCreateEnvironment}
				>
					<div className={styles.QuickActionContent}>
						<PlusOutlined className={styles.QuickActionIcon} />
						<div className={styles.QuickActionTitle}>{t('Overview.QuickActions.NewEnvironment')}</div>
						<div className={styles.QuickActionSubtitle}>
							{t('Overview.QuickActions.NewEnvironment.Subtitle')}
						</div>
					</div>
				</Card>
			</div>

			{/* Failed Processes Alert */}
			{displayMetrics.failedProcesses > 0 && (
				<Alert
					message={t('Overview.FailedProcesses.Title')}
					description={t('Overview.FailedProcesses.Description', { count: displayMetrics.failedProcesses })}
					type="error"
					showIcon
					icon={<ExclamationCircleOutlined />}
					action={
						<Button 
							type="link"
							size="small" 
							onClick={() => onNavigateToTab('processes')}
							className={styles.FailedProcessesButton}
						>
							{t('Overview.FailedProcesses.ViewDetails')} →
						</Button>
					}
					className={styles.FailedProcessesAlert}
				/>
			)}
			</div>
		</Spin>
	);
};

export default OverviewTab;

